import type { ReactNode } from "react";

/**
 * Simple line-art silhouettes per instrument category — one consistent style,
 * distinct shapes, so instrument cards don't all share the same generic icon.
 * viewBox is 24×36 (neck up, body down); stroke follows currentColor.
 */
const SILHOUETTES: Record<string, ReactNode> = {
  electric: (
    <>
      {/* angled headstock + neck */}
      <path d="M12 6V2.5L9.5 1v5" />
      <path d="M12 6v10" />
      {/* double-cutaway body */}
      <path d="M12 16c-2.2-1.8-5.4-1.4-6.6 1-1.4 2.8 0 4.6.2 6.6.3 3.4 2.6 6.4 6.4 6.4s6.1-3 6.4-6.4c.2-2 1.6-3.8.2-6.6-1.2-2.4-4.4-2.8-6.6-1z" />
      {/* bridge + pickup hint */}
      <path d="M9.5 24.5h5" />
    </>
  ),
  acoustic: (
    <>
      <path d="M10.5 1.5h3v4h-3z" />
      <path d="M12 5.5v8" />
      {/* figure-8 body */}
      <path d="M12 13.5c-3.4 0-5.2 1.9-5.2 4.3 0 1.6.9 2.4 1 3.8.3 3 1.5 6.4 4.2 6.4s3.9-3.4 4.2-6.4c.1-1.4 1-2.2 1-3.8 0-2.4-1.8-4.3-5.2-4.3z" />
      {/* soundhole */}
      <circle cx="12" cy="19.5" r="2" />
    </>
  ),
  bass: (
    <>
      {/* long neck, 2+2 tuners */}
      <path d="M12 1v19" />
      <path d="M9.5 2h5M9.5 4.5h5" />
      {/* compact offset body */}
      <path d="M12 20c-1.8-1.4-4.4-1-5.4 1-1.2 2.4-.2 3.8 0 5.4.3 2.8 2.2 5.1 5.4 5.1s5.1-2.3 5.4-5.1c.2-1.6 1.2-3 0-5.4-1-2-3.6-2.4-5.4-1z" />
    </>
  ),
  classical: (
    <>
      {/* slotted headstock */}
      <path d="M9.8 1.5h4.4v4.5H9.8z" />
      <path d="M11.2 2.5v2.5M12.8 2.5v2.5" />
      <path d="M12 6v7.5" />
      {/* wide figure-8 body */}
      <path d="M12 13.5c-3.8 0-5.6 2-5.6 4.4 0 1.7 1 2.5 1.1 4 .3 3.1 1.6 6.6 4.5 6.6s4.2-3.5 4.5-6.6c.1-1.5 1.1-2.3 1.1-4 0-2.4-1.8-4.4-5.6-4.4z" />
      <circle cx="12" cy="19.5" r="2.1" />
    </>
  ),
  ukulele: (
    <>
      {/* short neck, small body, high waist */}
      <path d="M10.8 5.5h2.4v3.5h-2.4z" />
      <path d="M12 9v6.5" />
      <path d="M12 15.5c-2.6 0-4 1.5-4 3.4 0 1.3.7 1.9.8 3 .2 2.4 1.2 5.1 3.2 5.1s3-2.7 3.2-5.1c.1-1.1.8-1.7.8-3 0-1.9-1.4-3.4-4-3.4z" />
      <circle cx="12" cy="20" r="1.6" />
    </>
  ),
};

export function InstrumentIcon({ category, size = 34 }: { category: string; size?: number }) {
  return (
    <svg
      width={(size * 24) / 36}
      height={size}
      viewBox="0 0 24 36"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {SILHOUETTES[category] ?? SILHOUETTES.electric}
    </svg>
  );
}
